/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { SyntheticEvent, useEffect, useRef } from 'react';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon/SvgIcon';

import { CMETabs } from './cmeTypes';
import CMECertificates from './CMECertificates';
import ClaimedCME from './ClaimedCME';
import styles from './CMETable.module.scss';
import ClaimableCME from './ClaimableCME';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  fetchCMEListThunk,
  fetchCMEPoints,
  getCMECertificateListThunk,
  selectActiveTab,
  selectCMEState,
  setActiveTab,
  setYear,
} from './cmeSlice';

const TabHeading = (title: string, loadingCount: boolean, count?: number | string) => (
  <span className={styles.tabHeading}>
    <span>{title}</span> <Avatar className="cme">{!loadingCount && count}</Avatar>
  </span>
);

export function CME() {
  const tabValue = useAppSelector(selectActiveTab);
  const { claimed, unclaimed, certificates, year, yearOptions, loadingCount } = useAppSelector(selectCMEState);
  const dispatch = useAppDispatch();

  const selectedYear = useRef(year);

  const handleChangeDrop = (event: SelectChangeEvent) => {
    dispatch(setYear(Number(event.target.value)));
  };

  const handleChange = (event: SyntheticEvent, newValue: CMETabs) => {
    dispatch(setActiveTab(newValue));
  };

  useEffect(() => {
    void dispatch(fetchCMEPoints());
    selectedYear.current = year;
  }, [year, dispatch]);

  useEffect(() => {
    switch (tabValue) {
      case 'unclaimed':
      case 'claimed':
        void dispatch(fetchCMEListThunk());
        break;
      case 'certificates':
        void dispatch(getCMECertificateListThunk());
        return;
    }
  }, [tabValue, year, dispatch]);

  const DropdownArrow = (props: SvgIconProps) => (
    <SvgIcon {...props} viewBox="-3 0 22 10">
      <svg fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="10">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.26298 -6.44176e-07C1.58636 -6.3004e-07 1.90973 0.12295 2.15648 0.369693L8 6.21406L13.8435 0.369694C14.337 -0.122951 15.137 -0.122951 15.6305 0.369694C16.1232 0.863181 16.1232 1.6632 15.6305 2.15669L8.8935 8.89371C8.40001 9.38635 7.59999 9.38635 7.1065 8.89371L0.369483 2.15669C-0.123161 1.6632 -0.123161 0.86318 0.369483 0.369693C0.616227 0.12295 0.939602 -6.58311e-07 1.26298 -6.44176e-07Z"
          fill="#A2BBD6"
        />
      </svg>
    </SvgIcon>
  );

  return (
    <Box className={styles.cmeTabsContainer}>
      <FormControl size="small" className={styles.yearOption}>
        <Select
          input={
            <Select
              inputProps={{
                'aria-label': 'cme year',
              }}
            />
          }
          value={year.toString()}
          displayEmpty
          onChange={handleChangeDrop}
          className={styles.dropDown}
          IconComponent={(props) => <DropdownArrow {...props} />}
        >
          {yearOptions.map((yearOpt) => (
            <MenuItem key={yearOpt} value={yearOpt}>
              {yearOpt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TabContext value={tabValue}>
        <Box>
          <TabList
            variant="scrollable"
            scrollButtons={false}
            indicatorColor="white"
            onChange={handleChange}
            className="CME lists"
          >
            <Tab
              label={TabHeading('CME: Ready to Claim', loadingCount, unclaimed?.toFixed(2))}
              value="unclaimed"
              className="cme"
            />
            <Tab
              label={TabHeading('CME: Claimed', loadingCount, claimed?.toFixed(2))}
              value="claimed"
              className="cme"
            />
            <Tab
              label={TabHeading('CME: Certificates', loadingCount, certificates)}
              value="certificates"
              className="cme"
            />
          </TabList>
        </Box>
        <TabPanel className="cme" value="unclaimed">
          <ClaimableCME />
        </TabPanel>
        <TabPanel className="cme" value="claimed">
          <ClaimedCME />
        </TabPanel>
        <TabPanel className="cme" value="certificates">
          <CMECertificates />
        </TabPanel>
      </TabContext>
    </Box>
  );
}

export default CME;
